import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { HTTP_BAD_REQUEST } from "../../common/api";
import {
  AnalyticsContext,
  ViewEmailConfirmationEvent,
  EmailConfirmationEnterCodeEvent,
  EmailConfirmationButtonTapEvent,
  EmailConfirmationRegenerateCodeButtonTapEvent,
  EmailConfirmationSuccessfulEvent,
  EmailConfirmationFailedEvent,
} from "../../common/context/AnalyticsContext";
import {
  Account,
  AccountContext,
  RetryBehavior,
} from "../../common/context/AccountContext";
import {
  AppFlowStepContainer,
  AppFlowTitle,
  AppFlowContinueButton,
  AppFlowSupportingText,
  AppFlowModal,
} from "../../common/CommonStyles";

const enum State {
  Input = 1,
  InProgress = 2,
  CodeError = 3,
  CodeNetworkError = 4,
  RegenerateError = 5,
  RegenerateNetworkError = 6,
  EmptyError = 7,
  Expired = 8,
  Success = 9,
}

const formatSecondsForDisplay = (seconds) => {
  const minutesText = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secondsText = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutesText}:${secondsText}`;
};
const calculateSecondsRemaining = (targetDateTime) => {
  const targetTime = new Date(targetDateTime).getTime();
  const now = new Date().getTime();
  return (targetTime - now) / 1000;
};

export const ConfirmationCode: React.FC<{
  account?: Account;
  next: () => void;
  previous: () => void;
}> = ({ account, next, previous }) => {
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);
  const shouldLogViewEmailConfirmationCode = React.useRef<boolean>(true);
  const didBeginEnteringCode = React.useRef(false);
  const [code, setCode] = React.useState("");
  const [state, setState] = React.useState(State.Input);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = React.useState<
    number | null
  >(null);
  const { makeRequest, agreeToTermsOfUse, updateUser } =
    React.useContext(AccountContext);

  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [showRegeneratedCodeModal, setShowRegeneratedCodeModal] =
    React.useState(false);

  if (shouldLogViewEmailConfirmationCode.current) {
    logEvent(new ViewEmailConfirmationEvent());
    shouldLogViewEmailConfirmationCode.current = false;
  }

  const requestNewCode = React.useCallback(async () => {
    if (state === State.InProgress) {
      // Intentionally empty to prevent double click
      return;
    }
    const date = account?.user?.unconfirmed_email_expiration_date;
    if (date !== null || date !== undefined) {
      const newTimeRemainingSeconds = calculateSecondsRemaining(
        account?.user?.unconfirmed_email_expiration_date || 0
      );
      if (newTimeRemainingSeconds <= 0) {
        setState(State.Expired);
        return;
      }
      setState(State.InProgress);
      const regenerateCodeResponse = await makeRequest(
        "auth/registration/email-confirmation-code/regenerate/",
        "POST",
        RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
        true,
        {} // There is no body
      );
      const updatedUser = regenerateCodeResponse.body;
      if (updatedUser === null || updatedUser === undefined) {
        if (regenerateCodeResponse.code === HTTP_BAD_REQUEST) {
          setState(State.RegenerateError);
        } else {
          setState(State.RegenerateNetworkError);
        }
      } else {
        updateUser(updatedUser, true, null, false);
        setShowRegeneratedCodeModal(true);
        setState(State.Input);
      }
    }
  }, [state, account, makeRequest, updateUser]);

  const verifyCode = React.useCallback(async () => {
    if (state === State.InProgress) {
      // Intentionally empty to prevent double click
    } else if (code.trim() === "") {
      setState(State.EmptyError);
    } else {
      setState(State.InProgress);
      const verifyResponse = await makeRequest(
        "auth/registration/email-confirmation-code/verify/",
        "POST",
        RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
        true,
        { confirmation_code: code }
      );
      const updatedUser = verifyResponse.body;
      if (updatedUser === null || updatedUser === undefined) {
        logEvent(new EmailConfirmationFailedEvent());

        if (verifyResponse.code === HTTP_BAD_REQUEST) {
          setState(State.CodeError);
        } else {
          setState(State.CodeNetworkError);
        }
      } else {
        logEvent(new EmailConfirmationSuccessfulEvent());
        agreeToTermsOfUse();
        updateUser(updatedUser, true, null, false);
        setState(State.Success);
      }
    }
  }, [
    code,
    state,
    setState,
    agreeToTermsOfUse,
    updateUser,
    makeRequest,
    logEvent,
  ]);

  React.useEffect(() => {
    const timerId = setInterval(() => {
      if (account !== undefined) {
        const newTimeRemainingSeconds = calculateSecondsRemaining(
          account.user.unconfirmed_email_expiration_date
        );
        if (newTimeRemainingSeconds >= 0) {
          setTimeRemainingSeconds(newTimeRemainingSeconds);
        } else {
          setTimeRemainingSeconds(0);
          updateUser(
            { ...account.user, has_unconfirmed_email: false },
            true,
            null,
            false
          );
          setState(State.Expired);
          clearInterval(timerId);
        }
      }
    }, 1000);

    return () => clearInterval(timerId);
  }, [account, timeRemainingSeconds, setTimeRemainingSeconds, updateUser]);

  React.useEffect(() => {
    const errorStates = [
      State.CodeNetworkError,
      State.EmptyError,
      State.CodeError,
      State.RegenerateNetworkError,
      State.RegenerateError,
      State.Expired,
    ];

    if (errorStates.includes(state)) {
      setShowErrorModal(true);
    } else if (state === State.Success) {
      next();
    }
  }, [state, setShowErrorModal, next]);

  return (
    <AppFlowStepContainer style={{ maxWidth: "700px", alignSelf: "center" }}>
      <AppFlowModal
        title={t("error_alert_title")}
        body={
          state === State.CodeNetworkError
            ? t("confirmation_code_default_error")
            : state === State.EmptyError
              ? t("confirmation_code_empty_error")
              : state === State.CodeError
                ? t("confirmation_code_default_error")
                : state === State.RegenerateNetworkError
                  ? t("confirmation_code_default_regenerate_error")
                  : state === State.RegenerateError
                    ? t("confirmation_code_default_regenerate_error")
                    : state === State.Expired
                      ? t("confirmation_code_expired_error")
                      : t("error_alert_title")
        }
        closeButtonText={t("generic_ok")}
        shouldShowModal={showErrorModal}
        closeModal={() => {
          if (state === State.Expired) {
            previous();
          } else {
            setState(State.Input);
          }
          setShowErrorModal(false);
        }}
      />
      <AppFlowModal
        title={t("confirmation_code_generated_title_text")}
        body={t("confirmation_code_generated_subtitle_text")}
        closeButtonText={t("generic_ok")}
        shouldShowModal={showRegeneratedCodeModal}
        closeModal={() => setShowRegeneratedCodeModal(false)}
      />
      <div className="h-100 d-flex flex-column justify-content-between">
        <AppFlowTitle
          title={t("confirmation_code_title_text")}
          subtitle={
            <Trans
              t={t}
              i18nKey="confirmation_code_subtitle_text"
              components={{
                email: (
                  <span>
                    {account?.user?.unconfirmed_email_for_display ||
                      t("confirmation_code_your_address_text")}
                  </span>
                ),
                linkText: (
                  <button
                    className="btn btn-link p-0 align-baseline text-decoration-none"
                    style={{
                      cursor: "pointer",
                      fontSize: "clamp(12px, 3vw, 16px)",
                      lineHeight: "22px",
                    }}
                    onClick={() => {
                      logEvent(
                        new EmailConfirmationRegenerateCodeButtonTapEvent()
                      );
                      requestNewCode();
                    }}
                  />
                ),
              }}
            />
          }
        />
        <div className="form-group my-3">
          <input
            type="text"
            className="form-control"
            placeholder={t("confirmation_code_code", {
              email:
                account?.user?.unconfirmed_email_for_display ||
                t("confirmation_code_your_address_text"),
            })}
            onChange={(e) => {
              if (!didBeginEnteringCode.current) {
                didBeginEnteringCode.current = true;
                logEvent(new EmailConfirmationEnterCodeEvent());
              }
              setCode(e.target.value);
            }}
          />
          {timeRemainingSeconds !== null && (
            <small className="form-text">
              <b>
                {t("confirmation_code_time_remaining_text", {
                  time: formatSecondsForDisplay(timeRemainingSeconds),
                })}
              </b>
            </small>
          )}
        </div>
        <div>
          <AppFlowSupportingText
            text={
              <Trans
                t={t}
                i18nKey="confirmation_code_legal_text"
                components={{
                  linkTextPrivacyPolicy: (
                    <Link
                      className="text-decoration-none"
                      style={{ cursor: "pointer" }}
                      to="/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  linkTextTermsOfUse: (
                    <Link
                      className="text-decoration-none"
                      style={{ cursor: "pointer" }}
                      to="/terms-of-use/"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            }
          />
        </div>
      </div>
      <div className="col-11 d-flex align-items-end justify-content-center flex-grow-1 mt-2">
        <AppFlowContinueButton
          text={t("app_flow_continue_button")}
          enabled={code.trim().length === 4}
          onClick={() => {
            logEvent(new EmailConfirmationButtonTapEvent());
            verifyCode();
          }}
        />
      </div>
    </AppFlowStepContainer>
  );
};
